import { ReactNode } from 'react';

import Badge from 'components/common/Badge';
import Card from 'components/common/Card';
import ErrorDisplay from 'components/common/ErrorDisplay';
import UserBubble from 'components/common/UserBubble';
import { useOrganizationCreditTransactions } from 'hooks/backend/billing';
import { OrganizationCreditTransactionsItem } from 'services/backend/billing';
import { DATE_FORMATS, formatDate } from 'util/dates';
import { formatDollars } from 'util/currency';
import { useCurrentOrganization } from 'hooks/backend/organizations';
import { Link } from 'react-router-dom';

const CREDIT_TRANSACTIONS_GRID_COLUMNS =
  'grid-cols-[160px_36px_130px_140px_140px_minmax(200px,1fr)_70px_70px]';

export const CreditTransactionsTable = () => {
  const currentOrg = useCurrentOrganization();
  const {
    creditTransactions,
    isLoadingCreditTransactions,
    isErrorCreditTransactions,
    creditTransactionsError,
  } = useOrganizationCreditTransactions({
    organizationId: currentOrg?.id,
  });

  const transactions = creditTransactions?.transactions ?? [];

  return (
    <Card>
      <div className="bg-white py-2 border-b border-gray-300 text-gray-600 font-normal">
        <CreditTransactionsGrid
          description="Description"
          invoiceId="Invoice ID"
          surveyId="Survey ID"
          totalCreditAmount="Total Credit Amount"
          transactionAmount="Transaction Amount"
          transactionDate="Transaction Date/Time"
          transactionType="Transaction Type"
          user="User"
        />
      </div>
      <div>
        {!currentOrg ? (
          <div className="py-4 px-4 text-sm text-gray-600">
            No organization selected.
          </div>
        ) : isLoadingCreditTransactions ? (
          <CreditTransactionsPlaceholderRows />
        ) : isErrorCreditTransactions ? (
          <div className="p-4">
            <ErrorDisplay
              message={`Failed to load credit transactions. ${creditTransactionsError?.message ?? ''}`}
            />
          </div>
        ) : transactions.length > 0 ? (
          transactions.map((transaction, index) => (
            <CreditTransactionsRow key={index} transaction={transaction} />
          ))
        ) : (
          <div className="py-4 px-4 text-sm text-gray-600">
            No credit transactions found.
          </div>
        )}
      </div>
    </Card>
  );
};

const CreditTransactionsGrid = ({
  transactionDate,
  user,
  transactionType,
  transactionAmount,
  totalCreditAmount,
  description,
  invoiceId,
  surveyId,
}: {
  transactionDate: ReactNode;
  user: ReactNode;
  transactionType: ReactNode;
  transactionAmount: ReactNode;
  totalCreditAmount: ReactNode;
  description: ReactNode;
  invoiceId: ReactNode;
  surveyId: ReactNode;
}) => {
  return (
    <div className={`grid gap-4 ${CREDIT_TRANSACTIONS_GRID_COLUMNS}`}>
      <div className="truncate">{transactionDate}</div>
      <div className="truncate">{user}</div>
      <div className="truncate">{transactionType}</div>
      <div className="truncate">{transactionAmount}</div>
      <div className="truncate">{totalCreditAmount}</div>
      <div className="truncate">{description}</div>
      <div className="truncate">{surveyId}</div>
      <div className="truncate">{invoiceId}</div>
    </div>
  );
};

const CreditTransactionsPlaceholderRows = () => {
  const rows = [1, 2, 3, 4];

  return rows.map((rowNum) => (
    <div key={rowNum} className="p-4 border-b border-gray-300 animate-pulse">
      <CreditTransactionsGrid
        description={<div className="w-3/4 h-4 bg-light-grey rounded" />}
        invoiceId={<div className="w-2/3 h-4 bg-light-grey rounded" />}
        surveyId={<div className="w-2/3 h-4 bg-light-grey rounded" />}
        totalCreditAmount={<div className="w-2/3 h-4 bg-light-grey rounded" />}
        transactionAmount={<div className="w-2/3 h-4 bg-light-grey rounded" />}
        transactionDate={<div className="w-3/4 h-4 bg-light-grey rounded" />}
        transactionType={<div className="w-1/2 h-4 bg-light-grey rounded" />}
        user={<div className="w-1/2 h-4 bg-light-grey rounded" />}
      />
    </div>
  ));
};

const CreditTransactionsRow = ({
  transaction,
}: {
  transaction: OrganizationCreditTransactionsItem;
}) => {
  return (
    <div className="py-4 border-b border-gray-300">
      <CreditTransactionsGrid
        description={transaction.description ?? 'N/A'}
        invoiceId={transaction.invoiceId ?? 'N/A'}
        surveyId={
          transaction.surveyId ? (
            <Link
              className="block mb-1 underline cursor-pointer"
              to={`/surveys/${transaction.surveyId}/build/review`}
            >
              {transaction.surveyId}
            </Link>
          ) : (
            'N/A'
          )
        }
        totalCreditAmount={formatDollars(transaction.totalCreditAmount / 100)}
        transactionAmount={formatDollars(transaction.transactionAmount / 100)}
        transactionDate={formatDate(transaction.transactionDate, {
          format: DATE_FORMATS.DATETIME,
        })}
        transactionType={
          <Badge
            color={
              transaction.transactionType === 'CREDIT' ? 'primary' : 'gray'
            }
            size="sm"
          >
            {transaction.transactionType}
          </Badge>
        }
        user={
          <div className="flex items-center space-x-2">
            <UserBubble user={transaction.user} />
          </div>
        }
      />
    </div>
  );
};
